import React from "react";
import "@/styles/native-home-v3.css";
import { LazyMotion, useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import {
  CalendarDays,
  ChevronRight,
  Clock3,
  Flag,
  ListChecks,
  LoaderCircle,
  Shield,
  Star,
  Trophy,
  Trash2,
  X,
} from "lucide-react";

import AdSlot from "@/components/AdSlot";
import FavoriteTeamDialog from "@/components/account/FavoriteTeamDialog";
import NativeAnnouncementDialog, {
  ANNOUNCEMENT_ICONS,
  ANNOUNCEMENT_LABELS,
} from "@/components/native/NativeAnnouncementDialog";
import { Skeleton } from "@/components/ui/skeleton";
import TeamLogo from "@/components/TeamLogo";
import { useHaptics } from "@/haptics/HapticsProvider";
import { useAccount } from "@/queries/account";
import { formatSvDate, formatSvTime, getSvYear, isSameSvDay, toDate } from "@/utils/date";

const loadMotionFeatures = () => import("@/lib/nativeMotionFeatures").then((module) => module.default);

function getTeamName(match, side) {
  return match?.[`${side}_team_display`]
    || match?.[`${side}_team_name_canonical`]
    || match?.[`${side}_team`]
    || "-";
}

function getTeamIdentities(match, side) {
  return [
    match?.[`${side}_team_id`],
    match?.[`${side}_team_name_canonical`],
    match?.[`${side}_team_display`],
    match?.[`${side}_team`],
  ]
    .map((value) => String(value || "").trim().toLocaleLowerCase("sv-SE"))
    .filter(Boolean);
}

function identitiesOverlap(first, second) {
  return first.some((identity) => second.includes(identity));
}

function getLatestMeeting(currentMatch, officialMatches) {
  const currentDate = toDate(currentMatch?.date);
  const currentSeason = getSvYear(currentDate);
  if (!currentDate || !currentSeason || !Array.isArray(officialMatches)) return null;

  const currentHome = getTeamIdentities(currentMatch, "home");
  const currentAway = getTeamIdentities(currentMatch, "away");

  return officialMatches
    .filter((candidate) => {
      const candidateDate = toDate(candidate?.date);
      if (!candidateDate || candidateDate.getTime() >= currentDate.getTime()) return false;
      if (getSvYear(candidateDate) !== currentSeason) return false;
      if (candidate?.home_score == null || candidate?.away_score == null) return false;

      const candidateHome = getTeamIdentities(candidate, "home");
      const candidateAway = getTeamIdentities(candidate, "away");
      return (
        identitiesOverlap(currentHome, candidateHome)
        && identitiesOverlap(currentAway, candidateAway)
      ) || (
        identitiesOverlap(currentHome, candidateAway)
        && identitiesOverlap(currentAway, candidateHome)
      );
    })
    .sort((first, second) => toDate(second.date).getTime() - toDate(first.date).getTime())[0] || null;
}

function getMatchStatus(match, fallback = "Kommande") {
  if (String(match?.manual_status || "").trim().toLowerCase() === "cancelled") {
    return { label: "Inställd", tone: "cancelled" };
  }
  const date = toDate(match?.date);
  if (date && !Number.isNaN(date.getTime()) && isSameSvDay(date, new Date())) {
    return { label: "Idag", tone: "today" };
  }
  return { label: fallback, tone: "upcoming" };
}

const PLAYOFF_STAGE_LABELS = {
  quarterfinal_series: "Kvartsfinal",
  semifinal_series: "Semifinal",
  final: "Final",
};

function getPlayoffStageLabel(match) {
  const stage = PLAYOFF_STAGE_LABELS[String(match?.stage_type || "").trim().toLowerCase()];
  if (!stage) return "";
  const leg = Number(match?.leg);
  return Number.isInteger(leg) && leg > 0 ? `${stage} · möte ${leg}` : stage;
}

function normalizeTeamIdentity(value) {
  return String(value || "").trim().toLocaleLowerCase("sv-SE");
}

function matchIncludesTeam(match, teamIdentities) {
  return identitiesOverlap(teamIdentities, getTeamIdentities(match, "home"))
    || identitiesOverlap(teamIdentities, getTeamIdentities(match, "away"));
}

function findTeamGroup(stage, teamIdentities) {
  return (stage?.groups || []).find((group) => (
    (group?.matches || []).some((match) => matchIncludesTeam(match, teamIdentities))
  )) || null;
}

function getGroupAggregate(group, teamIdentities) {
  let favoriteScore = 0;
  let opponentScore = 0;
  let scoredMatches = 0;

  (group?.matches || []).forEach((match) => {
    if (match?.home_score == null || match?.away_score == null) return;
    const homeScore = Number(match?.home_score);
    const awayScore = Number(match?.away_score);
    if (!Number.isFinite(homeScore) || !Number.isFinite(awayScore)) return;

    const favoriteIsHome = identitiesOverlap(teamIdentities, getTeamIdentities(match, "home"));
    const favoriteIsAway = identitiesOverlap(teamIdentities, getTeamIdentities(match, "away"));
    if (!favoriteIsHome && !favoriteIsAway) return;

    favoriteScore += favoriteIsHome ? homeScore : awayScore;
    opponentScore += favoriteIsHome ? awayScore : homeScore;
    scoredMatches += 1;
  });

  return scoredMatches ? `${favoriteScore}–${opponentScore}` : null;
}

function getGroupProgress(group, teamIdentities, expectedLegs) {
  const aggregate = getGroupAggregate(group, teamIdentities);
  const playedLegs = (group?.matches || []).filter((match) => (
    match?.home_score != null && match?.away_score != null
  )).length;
  const configuredLegs = Number(expectedLegs);
  const totalLegs = Number.isInteger(configuredLegs) && configuredLegs > 0
    ? configuredLegs
    : Math.max((group?.matches || []).length, playedLegs, 1);
  if (aggregate) return { metric: aggregate, metricLabel: `Möte ${playedLegs}/${totalLegs}` };

  const nextMatch = (group?.matches || []).find((match) => (
    match?.home_score == null || match?.away_score == null
  ));
  const leg = Number(nextMatch?.leg);
  return Number.isInteger(leg) && leg > 0
    ? { metric: `M${leg}`, metricLabel: `Möte ${playedLegs}/${totalLegs}` }
    : { metric: "PÅGÅR", metricLabel: "slutspel" };
}

function FavoriteTeamSkeleton() {
  return (
    <Skeleton className="native-v3-favorite-skeleton" aria-label="Laddar favoritlag" />
  );
}

function NativeHeroSkeleton() {
  return (
    <section className="native-v3-next-match native-v3-next-match-skeleton" aria-label="Laddar nästa match">
      <Skeleton className="native-v3-next-match-silhouette" />
    </section>
  );
}

function ResultTileSkeleton() {
  return (
    <article className="native-v3-result-tile native-v3-result-tile-skeleton" aria-hidden="true">
      <Skeleton className="native-v3-result-tile-silhouette" />
    </article>
  );
}

function ScheduleRowSkeleton() {
  return (
    <div className="native-v3-schedule-row native-v3-schedule-row-skeleton" aria-hidden="true">
      <Skeleton className="native-v3-schedule-row-silhouette" />
    </div>
  );
}

function NativeAnnouncement({ announcement, closing, onOpen, onDismiss }) {
  const reduceMotion = useReducedMotion();
  const { intensity: hapticIntensity, previewIntensity } = useHaptics();
  const thresholdReachedRef = React.useRef(false);
  const level = ANNOUNCEMENT_ICONS[announcement?.level] ? announcement.level : "info";
  const Icon = ANNOUNCEMENT_ICONS[level];
  const title = String(announcement?.title || "").trim() || "Meddelande";
  const dismissible = Boolean(announcement?.dismissible);

  const handleDrag = (_event, info) => {
    if (!dismissible) return;
    const reachedThreshold = info.offset.x <= -92;

    if (reachedThreshold && !thresholdReachedRef.current) {
      thresholdReachedRef.current = true;
      previewIntensity(hapticIntensity);
    } else if (info.offset.x > -74) {
      thresholdReachedRef.current = false;
    }
  };

  const handleDragEnd = (_event, info) => {
    if (!dismissible) return;
    thresholdReachedRef.current = false;
    if (info.offset.x <= -92) onDismiss();
  };

  return (
    <div className="native-v3-announcement-swipe" data-level={level} data-dismissible={dismissible ? "true" : "false"}>
      {dismissible ? (
        <div className="native-v3-announcement-swipe-action" aria-hidden="true">
          <Trash2 />
          <span>Dölj</span>
        </div>
      ) : null}
      <m.article
        className="native-v3-announcement"
        data-level={level}
        data-closing={closing ? "true" : "false"}
        drag={dismissible && !closing ? "x" : false}
        dragConstraints={{ left: -128, right: 0 }}
        dragDirectionLock
        dragElastic={{ left: 0.12, right: 0 }}
        dragMomentum={false}
        dragSnapToOrigin
        animate={{ x: closing ? "-110%" : 0 }}
        transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 430, damping: 38 }}
        onDragStart={() => {
          thresholdReachedRef.current = false;
        }}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
      >
        <button type="button" className="native-v3-announcement-open" onClick={onOpen}>
          <span className="native-v3-announcement-symbol" aria-hidden="true"><Icon /></span>
          <span className="native-v3-announcement-copy">
            <small>{ANNOUNCEMENT_LABELS[level]}</small>
            <strong>{title}</strong>
          </span>
          <ChevronRight className="native-v3-announcement-chevron" aria-hidden="true" />
        </button>
        {dismissible ? (
          <button type="button" className="native-v3-announcement-dismiss" onClick={onDismiss} aria-label="Stäng meddelande">
            <X />
          </button>
        ) : null}
      </m.article>
    </div>
  );
}

function FavoriteTeamSection({ standingsRows, officialMatches, playoffStages, playoffFormat, loadingStandings, loadingPlayoffs, onNavigate }) {
  const { data: account, isLoading: loadingAccount } = useAccount();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const favoriteId = String(account?.favorite_team_id || "");
  const favoriteIndex = standingsRows.findIndex((row) => String(row.team_id || "") === favoriteId);
  const favorite = favoriteIndex >= 0 ? standingsRows[favoriteIndex] : null;
  const favoriteName = String(favorite?.team || "").trim().toLowerCase();
  const favoriteIdentities = React.useMemo(
    () => [favoriteId, favoriteName].map(normalizeTeamIdentity).filter(Boolean),
    [favoriteId, favoriteName],
  );

  const nextFavoriteMatch = React.useMemo(() => {
    if (!favoriteId) return null;
    const now = Date.now();
    return [...officialMatches]
      .filter((match) => {
        if (String(match?.manual_status || "").toLowerCase() === "cancelled") return false;
        const time = toDate(match?.date)?.getTime();
        if (!Number.isFinite(time) || time < now) return false;
        const homeName = getTeamName(match, "home").trim().toLowerCase();
        const awayName = getTeamName(match, "away").trim().toLowerCase();
        return String(match?.home_team_id || "") === favoriteId
          || String(match?.away_team_id || "") === favoriteId
          || (favoriteName && (homeName === favoriteName || awayName === favoriteName));
      })
      .sort((a, b) => toDate(a.date).getTime() - toDate(b.date).getTime())[0] || null;
  }, [favoriteId, favoriteName, officialMatches]);

  const loading = loadingAccount || loadingStandings || loadingPlayoffs;
  const opponent = nextFavoriteMatch
    ? String(nextFavoriteMatch.home_team_id || "") === favoriteId
      || getTeamName(nextFavoriteMatch, "home").trim().toLowerCase() === favoriteName
      ? getTeamName(nextFavoriteMatch, "away")
      : getTeamName(nextFavoriteMatch, "home")
    : null;
  const nextPlayoffLabel = getPlayoffStageLabel(nextFavoriteMatch);
  const nextPlayoffShortLabel = {
    quarterfinal_series: "KF",
    semifinal_series: "SF",
    final: "F",
  }[String(nextFavoriteMatch?.stage_type || "").trim().toLowerCase()] || "";
  const seasonStatus = React.useMemo(() => {
    const stages = Array.isArray(playoffStages) ? playoffStages : [];
    const qf = stages.find((stage) => stage?.stage_type === "quarterfinal_series");
    const sf = stages.find((stage) => stage?.stage_type === "semifinal_series");
    const final = stages.find((stage) => stage?.stage_type === "final");
    const expectedLegs = (stageType) => (
      playoffFormat?.playoff_stages || []
    ).find((stage) => stage?.key === stageType)?.legs_per_group;
    const qfGroup = findTeamGroup(qf, favoriteIdentities);
    const sfGroup = findTeamGroup(sf, favoriteIdentities);
    const finalGroup = findTeamGroup(final, favoriteIdentities);
    const winnerIsFavorite = (group) => favoriteIdentities.includes(
      normalizeTeamIdentity(group?.winner?.winner_team),
    );
    const nextDescription = nextFavoriteMatch
      ? `${opponent} · ${formatSvDate(nextFavoriteMatch.date, { day: "numeric", month: "short" })}`
      : null;

    if (finalGroup?.winner?.requires_resolution) {
      return { marker: "F", detail: "Avgörande väntar", ...getGroupProgress(finalGroup, favoriteIdentities, expectedLegs("final")) };
    }
    if (finalGroup?.complete) {
      if (!finalGroup?.winner?.winner_team) {
        return { marker: "F", detail: "Avgörande väntar", ...getGroupProgress(finalGroup, favoriteIdentities, expectedLegs("final")) };
      }
      if (winnerIsFavorite(finalGroup)) {
        const finalYear = getSvYear(toDate(finalGroup.matches?.[0]?.date));
        return { marker: "★", detail: `Mästare${finalYear ? ` ${finalYear}` : ""}`, metric: "GULD", metricLabel: "Bauhausligan" };
      }
      return { marker: "F", detail: "Finalist", metric: "SILVER", metricLabel: "Bauhausligan" };
    }
    if (finalGroup) {
      return { marker: "F", detail: nextDescription ? `Nästa: ${nextDescription}` : "Finalen pågår", ...getGroupProgress(finalGroup, favoriteIdentities, expectedLegs("final")) };
    }

    if (sfGroup?.winner?.requires_resolution) {
      return { marker: "SF", detail: "Avgörande väntar", ...getGroupProgress(sfGroup, favoriteIdentities, expectedLegs("semifinal_series")) };
    }
    if (sfGroup?.complete) {
      if (!sfGroup?.winner?.winner_team) {
        return { marker: "SF", detail: "Avgörande väntar", ...getGroupProgress(sfGroup, favoriteIdentities, expectedLegs("semifinal_series")) };
      }
      return winnerIsFavorite(sfGroup)
        ? { marker: "SF", detail: "Klar för final", metric: "VIDARE", metricLabel: "semifinal" }
        : { marker: "SF", detail: "Utslaget i semifinal", metric: "SLUT", metricLabel: "semifinal" };
    }
    if (sfGroup) {
      return { marker: "SF", detail: nextDescription ? `Nästa: ${nextDescription}` : "Semifinalen pågår", ...getGroupProgress(sfGroup, favoriteIdentities, expectedLegs("semifinal_series")) };
    }

    const qfRow = (qf?.standings?.rows || []).find((row) => (
      favoriteIdentities.includes(normalizeTeamIdentity(row?.team_id))
      || favoriteIdentities.includes(normalizeTeamIdentity(row?.team))
    ));
    const qfQualifyingPositions = (sf?.groups || [])
      .flatMap((group) => [group?.home_seed_source, group?.away_seed_source])
      .map((source) => {
        const [scope, position] = String(source || "").split(":");
        return scope === "quarterfinal_series" ? Number(position) : null;
      })
      .filter(Number.isInteger);
    const qfQualificationCutoff = qfQualifyingPositions.length
      ? Math.max(...qfQualifyingPositions)
      : 4;
    if (qf?.complete && qfGroup) {
      const qfPosition = Number(qfRow?.position);
      return Number.isInteger(qfPosition) && qfPosition <= qfQualificationCutoff
        ? { marker: "KF", detail: "Klar för semifinal", metric: `#${qfPosition}`, metricLabel: "KF-tabell" }
        : { marker: "KF", detail: "Utslaget i kvartsfinal", metric: Number.isInteger(qfPosition) ? `#${qfPosition}` : "SLUT", metricLabel: "KF-tabell" };
    }
    if (qfGroup) {
      return { marker: "KF", detail: nextDescription ? `Nästa: ${nextDescription}` : "Kvartsfinalen pågår", ...getGroupProgress(qfGroup, favoriteIdentities, expectedLegs("quarterfinal_series")) };
    }

    const qfGroups = qf?.groups || [];
    const qfFieldIsPublished = qfGroups.length > 0
      && qfGroups.every((group) => (group?.matches || []).length > 0);
    if (qfFieldIsPublished) {
      return { marker: `#${favoriteIndex + 1}`, detail: "Ej till slutspel", metric: "SLUT", metricLabel: "grundserie" };
    }

    return {
      marker: nextPlayoffShortLabel || `#${favoriteIndex + 1}`,
      detail: nextFavoriteMatch ? `${nextPlayoffLabel || "Nästa"}: ${nextDescription}` : null,
      metric: `${favorite?.points ?? 0} p`,
      metricLabel: `${favorite?.played ?? 0} matcher`,
    };
  }, [favorite, favoriteIdentities, favoriteIndex, nextFavoriteMatch, nextPlayoffLabel, nextPlayoffShortLabel, opponent, playoffFormat, playoffStages]);

  return (
    <section className="native-v3-favorite-section">
      <div className="native-v3-eyebrow"><Star aria-hidden="true" /> Ditt lag</div>
      {loading ? <FavoriteTeamSkeleton /> : favorite ? (
        <button
          type="button"
          className="native-v3-favorite-summary"
          onClick={() => onNavigate(`/serie/lag/${favoriteId}`)}
          aria-label={`Öppna lagsidan för ${favorite.team}`}
        >
          <span className="native-v3-favorite-rank">{seasonStatus.marker}</span>
          <TeamLogo teamName={favorite.team} size={54} className="native-v3-favorite-logo" fallback={<Shield aria-hidden="true" />} />
          <span className="native-v3-favorite-copy">
            <strong>{favorite.team}</strong>
            <small>{favorite.city || "Bauhausligan"}</small>
            {seasonStatus.detail ? <em>{seasonStatus.detail}</em> : null}
          </span>
          <span className="native-v3-favorite-points">
            <strong>{seasonStatus.metric}</strong>
            <small>{seasonStatus.metricLabel}</small>
          </span>
          <ChevronRight aria-hidden="true" />
        </button>
      ) : (
        <button
          type="button"
          className="native-v3-favorite-empty"
          data-native-onboarding-target="favorite-team"
          onClick={() => setPickerOpen(true)}
        >
          <span><Star aria-hidden="true" /></span>
          <span><strong>Välj favoritlag</strong><small>Få en snabbare överblick över laget du följer.</small></span>
          <ChevronRight aria-hidden="true" />
        </button>
      )}
      <FavoriteTeamDialog
        open={pickerOpen}
        onOpenChange={(nextOpen) => {
          setPickerOpen(nextOpen);
          if (!nextOpen) {
            window.dispatchEvent(new CustomEvent("native-onboarding:favorite-team-dialog-closed"));
          }
        }}
      />
    </section>
  );
}

function NativeHero({ matches, officialMatches, activeIndex, onApiReady, onOpenMatch, loading, error }) {
  const scrollerRef = React.useRef(null);
  const [nativeActiveIndex, setNativeActiveIndex] = React.useState(activeIndex || 0);

  React.useEffect(() => {
    onApiReady(null);
    return () => onApiReady(null);
  }, [onApiReady]);

  const scrollToMatch = React.useCallback((index) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTo({ left: scroller.clientWidth * index, behavior: "smooth" });
  }, []);

  React.useEffect(() => {
    if (matches.length <= 1) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const timeoutId = window.setTimeout(() => {
      scrollToMatch((nativeActiveIndex + 1) % matches.length);
    }, 6300);
    return () => window.clearTimeout(timeoutId);
  }, [matches.length, nativeActiveIndex, scrollToMatch]);

  const handleNativeScroll = React.useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller?.clientWidth) return;
    const nextIndex = Math.max(0, Math.min(matches.length - 1, Math.round(scroller.scrollLeft / scroller.clientWidth)));
    setNativeActiveIndex((current) => current === nextIndex ? current : nextIndex);
  }, [matches.length]);

  if (error) return <div className="native-v3-home-error">Kunde inte ladda kommande matcher.</div>;
  if (loading) return <NativeHeroSkeleton />;
  if (!matches.length) {
    return (
      <section className="native-v3-next-match native-v3-next-match-empty">
        <div className="native-v3-next-label">Nästa match</div>
        <div className="native-v3-track-surface" aria-hidden="true" />
        <div className="native-v3-next-empty-content">
          <CalendarDays aria-hidden="true" />
          <strong>Inga kommande matcher just nu</strong>
          <span>Nya officiella matcher visas här när de blir tillgängliga.</span>
        </div>
      </section>
    );
  }

  return (
    <section className="native-v3-next-match">
      <div className="native-v3-next-label">Nästa match</div>
      <div className="native-v3-track-surface" aria-hidden="true" />
      <div ref={scrollerRef} className="native-v3-next-viewport" onScroll={handleNativeScroll}>
        <div className="native-v3-next-track">
          {matches.map((match) => {
            const id = match.id || match._id;
            const homeTeam = getTeamName(match, "home");
            const awayTeam = getTeamName(match, "away");
            const status = getMatchStatus(match);
            const stageLabel = getPlayoffStageLabel(match);
            const latestMeeting = getLatestMeeting(match, officialMatches);
            return (
              <div key={id} className="native-v3-next-native-slide">
                <div className="native-v3-next-slide">
                  <div className="native-v3-next-kicker">
                    <span data-tone={status.tone}>{status.label}</span>
                    <div className="native-v3-next-kicker-meta">
                      {stageLabel ? <em>{stageLabel}</em> : null}
                      <small>{match.league || "Bauhausligan"}{match.round ? ` · ${match.round}` : ""}</small>
                    </div>
                  </div>
                  <div className="native-v3-next-versus">
                    <div className="native-v3-next-team">
                      <TeamLogo teamName={homeTeam} size={70} />
                      <strong>{homeTeam}</strong>
                      <small>Hemma</small>
                    </div>
                    <div className="native-v3-next-vs"><span>VS</span></div>
                    <div className="native-v3-next-team">
                      <TeamLogo teamName={awayTeam} size={70} />
                      <strong>{awayTeam}</strong>
                      <small>Borta</small>
                    </div>
                  </div>
                  <div className="native-v3-next-meta">
                    <span><CalendarDays aria-hidden="true" />{formatSvDate(match.date, { weekday: "short", day: "numeric", month: "short" })}</span>
                    <i />
                    <span><Clock3 aria-hidden="true" />{formatSvTime(match.date, { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                  {latestMeeting ? (
                    <div className="native-v3-next-meeting">
                      <span className="native-v3-next-meeting-label">Senaste mötet</span>
                      <div className="native-v3-next-meeting-result">
                        <TeamLogo teamName={getTeamName(latestMeeting, "home")} size={24} />
                        <strong>{latestMeeting.home_score}</strong>
                        <i>–</i>
                        <strong>{latestMeeting.away_score}</strong>
                        <TeamLogo teamName={getTeamName(latestMeeting, "away")} size={24} />
                      </div>
                      <small>{formatSvDate(latestMeeting.date, { day: "numeric", month: "short" })}</small>
                    </div>
                  ) : null}
                  <button type="button" className="native-v3-next-action" onClick={() => onOpenMatch(id)}>
                    Se matchkort <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {matches.length > 1 ? (
        <div className="native-v3-next-pagination" aria-label={`Match ${nativeActiveIndex + 1} av ${matches.length}`}>
          {matches.map((match, index) => (
            <button type="button" key={match.id || match._id} aria-label={`Gå till match ${index + 1}`} data-active={index === nativeActiveIndex ? "true" : "false"} onClick={() => scrollToMatch(index)} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function SectionHeading({ eyebrow, title, action }) {
  return (
    <div className="native-v3-section-heading">
      <div>{eyebrow ? <span>{eyebrow}</span> : null}<h2 className="native-v3-display">{title}</h2></div>
      {action || null}
    </div>
  );
}

export function NativeHomePageSkeleton() {
  return (
    <div className="native-v3-home" aria-label="Laddar startsidan">
      <FavoriteTeamSkeleton />
      <NativeHeroSkeleton />
      <Skeleton className="native-v3-home-ad-skeleton" aria-hidden="true" />

      <section className="native-v3-flat-section">
        <SectionHeading eyebrow="Senast avgjort" title="Senaste resultat" />
        <div className="native-v3-result-rail" aria-hidden="true">
          {[0, 1, 2].map((item) => <ResultTileSkeleton key={item} />)}
        </div>
      </section>

      <section className="native-v3-flat-section">
        <SectionHeading eyebrow="Snabbt läge" title="Översikt" />
        <Skeleton className="native-v3-overview-skeleton" aria-hidden="true" />
      </section>

      <section className="native-v3-flat-section native-v3-schedule-section">
        <SectionHeading eyebrow="Framåt" title="Matchtablå" />
        <div className="native-v3-schedule-list" aria-hidden="true">
          {[0, 1, 2].map((item) => <ScheduleRowSkeleton key={item} />)}
        </div>
      </section>
    </div>
  );
}

export default function NativeHomePage({
  announcements,
  closingAnnouncementIds,
  onDismissAnnouncement,
  loadingOfficial,
  errorOfficial,
  heroMatches,
  heroIndex,
  onHeroApi,
  onOpenMatch,
  loadingMatches,
  protocolCount,
  loadingStandings,
  topTeamSummary,
  onNavigate,
  recentMatches,
  displayedRecentMatches,
  recentScrollerRef,
  recentLoaderRef,
  hasMoreRecent,
  loadingMoreRecent,
  displayedUpcoming,
  hasMoreUpcoming,
  onShowMoreUpcoming,
  standingsRows,
  officialMatches,
  playoffStages,
  playoffFormat,
  loadingPlayoffs,
}) {
  const [selectedAnnouncement, setSelectedAnnouncement] = React.useState(null);

  const dismissAnnouncement = React.useCallback((announcement) => {
    setSelectedAnnouncement(null);
    onDismissAnnouncement(announcement);
  }, [onDismissAnnouncement]);

  if (loadingOfficial && loadingMatches && loadingStandings) {
    return <NativeHomePageSkeleton />;
  }

  return (
    <div className="native-v3-home">
      {announcements.length ? (
        <LazyMotion features={loadMotionFeatures} strict>
          <div className="native-v3-home-announcements">
            {announcements.map((announcement) => {
              const id = `${announcement?.id || ""}:${announcement?.version || 0}`;
              return (
                <NativeAnnouncement
                  key={id}
                  announcement={announcement}
                  closing={closingAnnouncementIds.includes(id)}
                  onOpen={() => setSelectedAnnouncement(announcement)}
                  onDismiss={() => dismissAnnouncement(announcement)}
                />
              );
            })}
          </div>
        </LazyMotion>
      ) : null}

      <NativeAnnouncementDialog
        announcement={selectedAnnouncement}
        open={Boolean(selectedAnnouncement)}
        onOpenChange={(open) => {
          if (!open) setSelectedAnnouncement(null);
        }}
        onDismiss={dismissAnnouncement}
      />

      <FavoriteTeamSection
        standingsRows={standingsRows}
        officialMatches={officialMatches}
        playoffStages={playoffStages}
        playoffFormat={playoffFormat}
        loadingStandings={loadingStandings}
        loadingPlayoffs={loadingPlayoffs}
        onNavigate={onNavigate}
      />
      <NativeHero matches={heroMatches} officialMatches={officialMatches} activeIndex={heroIndex} onApiReady={onHeroApi} onOpenMatch={onOpenMatch} loading={loadingOfficial} error={errorOfficial} />

      <AdSlot placement="banner_home" slotKey="home-hero" className="native-v3-home-ad" refreshMs={45000} minSwapMs={30000} lazy />

      <section className="native-v3-flat-section">
        <SectionHeading eyebrow="Senast avgjort" title="Senaste resultat" />
        {loadingOfficial ? (
          <div className="native-v3-result-rail" aria-label="Laddar senaste resultat">
            {[0, 1, 2].map((item) => <ResultTileSkeleton key={item} />)}
          </div>
        ) : recentMatches.length ? (
          <div ref={recentScrollerRef} className="native-v3-result-rail">
            {displayedRecentMatches.map((match) => {
              const homeTeam = getTeamName(match, "home");
              const awayTeam = getTeamName(match, "away");
              return (
                <article key={match.id || match._id} className="native-v3-result-tile">
                  <div><TeamLogo teamName={homeTeam} size={30} /><strong>{match.home_score}</strong></div>
                  <span>{formatSvDate(match.date, { day: "numeric", month: "short" })}</span>
                  <div><strong>{match.away_score}</strong><TeamLogo teamName={awayTeam} size={30} /></div>
                </article>
              );
            })}
            {hasMoreRecent ? <div ref={recentLoaderRef} className="native-v3-home-rail-loader"><LoaderCircle className={loadingMoreRecent ? "animate-spin" : ""} /></div> : null}
          </div>
        ) : (
          <div className={errorOfficial ? "native-v3-home-error" : "native-v3-home-empty-row"}>
            {errorOfficial ? "Kunde inte ladda senaste resultat." : "Inga avslutade matcher att visa."}
          </div>
        )}
      </section>

      <section className="native-v3-flat-section">
        <SectionHeading eyebrow="Snabbt läge" title="Översikt" />
        <div className="native-v3-overview-strip">
          <button type="button" onClick={() => onNavigate("/account/matches")}>
            <ListChecks aria-hidden="true" /><span>{loadingMatches ? <Skeleton className="h-5 w-8" /> : <strong>{protocolCount}</strong>}<small>protokoll</small></span>
          </button>
          <button type="button" onClick={() => onNavigate("/tabell/tabell")}>
            <Trophy aria-hidden="true" /><span>{loadingStandings ? <Skeleton className="h-5 w-8" /> : <strong>{topTeamSummary.value}</strong>}<small>{topTeamSummary.title}</small></span>
          </button>
          <button type="button" onClick={() => onNavigate("/serie")}>
            <Flag aria-hidden="true" /><span>{loadingStandings ? <Skeleton className="h-5 w-8" /> : <strong>{standingsRows.length}</strong>}<small>lag i serien</small></span>
          </button>
        </div>
      </section>

      <section className="native-v3-flat-section native-v3-schedule-section">
        <SectionHeading eyebrow="Framåt" title="Matchtablå" />
        <div className="native-v3-schedule-list">
          {errorOfficial ? <div className="native-v3-home-error">Kunde inte ladda officiella matcher.</div> : null}
          {loadingOfficial ? [0, 1, 2].map((item) => <ScheduleRowSkeleton key={item} />) : null}
          {!loadingOfficial && !errorOfficial ? displayedUpcoming.map((match) => {
            const id = match.id || match._id;
            const homeTeam = getTeamName(match, "home");
            const awayTeam = getTeamName(match, "away");
            const stageLabel = getPlayoffStageLabel(match);
            return (
              <button type="button" key={id} className="native-v3-schedule-row" onClick={() => onOpenMatch(id)}>
                <span className="native-v3-schedule-team"><TeamLogo teamName={homeTeam} size={30} /><strong>{homeTeam}</strong></span>
                <span className="native-v3-schedule-time">
                  <strong>{formatSvTime(match.date, { hour: "2-digit", minute: "2-digit" })}</strong>
                  <small>{formatSvDate(match.date, { day: "numeric", month: "short" })}</small>
                  {stageLabel ? <em>{stageLabel}</em> : null}
                </span>
                <span className="native-v3-schedule-team native-v3-schedule-away"><strong>{awayTeam}</strong><TeamLogo teamName={awayTeam} size={30} /></span>
                <ChevronRight aria-hidden="true" />
              </button>
            );
          }) : null}
          {!loadingOfficial && !errorOfficial && !displayedUpcoming.length ? <div className="native-v3-home-empty-row">Inga kommande officiella matcher.</div> : null}
        </div>
        {hasMoreUpcoming ? <button type="button" className="native-v3-text-action" onClick={onShowMoreUpcoming}>Visa fler matcher <ChevronRight aria-hidden="true" /></button> : null}
      </section>
    </div>
  );
}
